'use client';

import * as React from 'react';

/** The day-section contract: `section[data-local-date="YYYY-MM-DD"]`. */
export const DAY_SECTION_SELECTOR = 'section[data-local-date]';

function readLoadedDates(root: ParentNode): string[] {
  const dates = new Set<string>();
  root.querySelectorAll<HTMLElement>(DAY_SECTION_SELECTOR).forEach((section) => {
    const date = section.dataset.localDate;
    if (date) dates.add(date);
  });
  return [...dates].sort();
}

function sameDates(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((date, i) => date === b[i]);
}

/**
 * WEB-1079 — the city-local dates of every day section currently in the DOM,
 * sorted ascending.
 *
 * Loaded days are read from the DOM (`[data-local-date]`) rather than lifted
 * out of the list's pagination state: the list owns its pages, and the strip
 * only needs to know which sections exist.
 *
 * The observer watches `document.body`, and every read re-resolves
 * `rootSelector`. The results region can be remounted (a view toggle, a
 * Suspense swap); an observer bound to the region node it found at mount
 * would keep watching the detached node, and loaded chips would freeze. Reads
 * are coalesced to one per animation frame, and the returned array keeps its
 * identity until the set of dates actually changes, so unrelated DOM churn
 * costs one `querySelectorAll` and no re-render.
 *
 * @param rootSelector the region to read; `document` when absent or unmounted.
 */
export function useLoadedDayDates(rootSelector?: string): readonly string[] {
  const [dates, setDates] = React.useState<readonly string[]>([]);

  React.useEffect(() => {
    const sync = (): void => {
      const root: ParentNode =
        (rootSelector ? document.querySelector(rootSelector) : null) ?? document;
      const next = readLoadedDates(root);
      setDates((prev) => (sameDates(prev, next) ? prev : next));
    };
    let frame = 0;
    const schedule = (): void => {
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        sync();
      });
    };
    sync();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-local-date'],
    });
    return () => {
      observer.disconnect();
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [rootSelector]);

  return dates;
}
